// packages/ui/src/inputs/OutlinedTextField.tsx
'use client';

// MUI's TextField drawn its outlined, non-select way only. TextField imports Select
// (and with it Menu, Popover and Modal) and its filled and standard inputs, to
// render whichever is asked for; a widget whose text fields are all outlined and
// none a select draws them with this instead, and its bundle carries none of that
// (docs/embed-size-and-speed.md §4, textfield-outlined-only: reviews.js −10 KB,
// chat.js −21 KB). Keep it to the chat and review widgets' components: the
// shop draws selects (FormSelectField), so it keeps MUI's TextField, and this
// would only add to main.js.
//
// It is TextField's outlined path (@mui/material 7.3, TextField/TextField.js) with
// the same markup, classes, ids and ARIA wiring: the theme's `MuiTextField` entry
// (default props, style overrides), `slots`/`slotProps` (an object or a function
// of the owner state, as MUI's `useSlot` merges them) and the legacy
// `InputProps`/`inputProps`/`InputLabelProps`/`FormHelperTextProps` that
// Autocomplete's `renderInput` passes. OutlinedTextField.test.ts compares its
// server-rendered markup with TextField's: re-run it after every MUI upgrade.
import { forwardRef, type ElementType, type Ref } from 'react';
import { unstable_composeClasses as composeClasses } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useDefaultProps } from '@mui/material/DefaultPropsProvider';
import { styled } from '@mui/material/styles';
import { getTextFieldUtilityClass, type TextFieldProps } from '@mui/material/TextField';
import { unstable_useId as useId, useForkRef } from '@mui/material/utils';

/** TextField's props, without the select and the other variants. */
export type OutlinedTextFieldProps = Omit<TextFieldProps, 'select' | 'SelectProps' | 'variant' | 'children'> & {
  variant?: 'outlined';
};

type SlotName = 'root' | 'input' | 'inputLabel' | 'htmlInput' | 'formHelperText';
type SlotProps = Record<string, unknown> & { className?: string; style?: object; ref?: Ref<unknown>; component?: ElementType };
type SlotPropsOrCallback = SlotProps | ((ownerState: object) => SlotProps) | undefined;

const TextFieldRoot = styled(FormControl, { name: 'MuiTextField', slot: 'Root' })({});

const joinClassNames = (...names: Array<string | undefined>) => names.filter(Boolean).join(' ');

/** MUI's `useSlot` for one of TextField's slots, without the internal event handlers TextField never passes. */
function useSlotProps(
  name: SlotName,
  elementType: ElementType,
  slotProps: SlotPropsOrCallback,
  ownerState: object,
  { className, additionalProps, forwardedProps, ref }: { className?: string; additionalProps?: SlotProps; forwardedProps?: SlotProps; ref?: Ref<unknown> } = {},
): Record<string, unknown> {
  const resolved = typeof slotProps === 'function' ? slotProps(ownerState) : slotProps;
  const merged: SlotProps = { ...additionalProps, ...forwardedProps, ...resolved };
  const joinedClassName = joinClassNames(additionalProps?.className, className, forwardedProps?.className, resolved?.className);
  if (joinedClassName.length > 0) merged.className = joinedClassName;
  const style = { ...additionalProps?.style, ...forwardedProps?.style, ...resolved?.style };
  if (Object.keys(style).length > 0) merged.style = style;
  const { component, ...props } = merged;
  const forkedRef = useForkRef(resolved?.ref, ref);
  const leafComponent = name === 'root' ? (component ?? forwardedProps?.component) : component;
  const withComponent = leafComponent ? (name === 'root' ? { component: leafComponent } : { as: leafComponent }) : {};
  const all = { ...props, ...withComponent, ref: forkedRef };
  return typeof elementType === 'string' ? all : { ...all, ownerState: { ...(props.ownerState as object), ...ownerState } };
}

export const OutlinedTextField = forwardRef<HTMLDivElement, OutlinedTextFieldProps>(function OutlinedTextField(inProps, ref) {
  const props = useDefaultProps({ props: inProps, name: 'MuiTextField' });
  if (process.env.NODE_ENV !== 'production') {
    const asked = props as { select?: boolean; variant?: string };
    if (asked.select || (asked.variant !== undefined && asked.variant !== 'outlined')) {
      console.error("OutlinedTextField draws outlined text fields only: a select or another variant needs MUI's TextField.");
    }
  }
  const {
    autoComplete,
    autoFocus = false,
    className,
    color = 'primary',
    defaultValue,
    disabled = false,
    error = false,
    // The legacy props are TextField's API still (Autocomplete's renderInput passes three of them), so read as TextField does.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- TextField's legacy prop, supported as TextField supports it
    FormHelperTextProps: FormHelperTextPropsProp,
    fullWidth = false,
    helperText,
    id: idOverride,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- TextField's legacy prop, supported as TextField supports it
    InputLabelProps: InputLabelPropsProp,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- TextField's legacy prop, supported as TextField supports it
    inputProps: inputPropsProp,
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- TextField's legacy prop, supported as TextField supports it
    InputProps: InputPropsProp,
    inputRef,
    label,
    maxRows,
    minRows,
    multiline = false,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required = false,
    rows,
    slots = {},
    slotProps = {},
    type,
    value,
    variant = 'outlined',
    ...other
  } = props;
  const ownerState = { ...props, autoFocus, color, disabled, error, fullWidth, multiline, required, select: false, variant };
  const rootClassName = composeClasses({ root: ['root'] }, getTextFieldUtilityClass, props.classes).root;

  const id = useId(idOverride);
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  const inputLabelId = label && id ? `${id}-label` : undefined;
  const allSlotProps = {
    input: InputPropsProp,
    inputLabel: InputLabelPropsProp,
    htmlInput: inputPropsProp,
    formHelperText: FormHelperTextPropsProp,
    ...slotProps,
  } as Record<SlotName, SlotPropsOrCallback>;
  const slotTypes = slots as Partial<Record<SlotName, ElementType>>;

  const inputAdditionalProps: SlotProps = {};
  const inputLabelSlotProps = allSlotProps.inputLabel;
  if (inputLabelSlotProps && typeof inputLabelSlotProps !== 'function' && typeof inputLabelSlotProps.shrink !== 'undefined') {
    inputAdditionalProps.notched = inputLabelSlotProps.shrink;
  }
  inputAdditionalProps.label = label;

  const RootSlot = slotTypes.root ?? TextFieldRoot;
  const rootProps = useSlotProps('root', RootSlot, allSlotProps.root, ownerState, {
    className: joinClassNames(rootClassName, className),
    additionalProps: { disabled, error, fullWidth, required, color, variant },
    forwardedProps: other as SlotProps,
    ref,
  });
  const InputSlot = slotTypes.input ?? OutlinedInput;
  const inputProps = useSlotProps('input', InputSlot, allSlotProps.input, ownerState, { additionalProps: inputAdditionalProps });
  const InputLabelSlot = slotTypes.inputLabel ?? InputLabel;
  const inputLabelProps = useSlotProps('inputLabel', InputLabelSlot, allSlotProps.inputLabel, ownerState);
  const HtmlInputSlot = slotTypes.htmlInput ?? 'input';
  const htmlInputProps = useSlotProps('htmlInput', HtmlInputSlot, allSlotProps.htmlInput, ownerState);
  const FormHelperTextSlot = slotTypes.formHelperText ?? FormHelperText;
  const formHelperTextProps = useSlotProps('formHelperText', FormHelperTextSlot, allSlotProps.formHelperText, ownerState);

  return (
    <RootSlot {...rootProps}>
      {label != null && label !== '' && (
        <InputLabelSlot htmlFor={id} id={inputLabelId} {...inputLabelProps}>
          {label}
        </InputLabelSlot>
      )}
      <InputSlot
        aria-describedby={helperTextId}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        fullWidth={fullWidth}
        multiline={multiline}
        name={name}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        type={type}
        value={value}
        id={id}
        inputRef={inputRef}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        inputProps={htmlInputProps}
        slots={{ input: slotTypes.htmlInput ? HtmlInputSlot : undefined }}
        {...inputProps}
      />
      {helperText && (
        <FormHelperTextSlot id={helperTextId} {...formHelperTextProps}>
          {helperText}
        </FormHelperTextSlot>
      )}
    </RootSlot>
  );
});
